import type { ReactNode } from 'react';

import type { DataGridCellContext } from '../types';

// The column declares the format; the value cannot be inspected for it, because a midnight
// timestamp and a date-only value are identical at runtime.
//
// `'date'` is a calendar date pinned to UTC and never converted: formatting it in the viewer's
// zone renders the previous day for everyone behind UTC. `'date-time'` is an instant, so it does
// belong in the viewer's zone — `meta.timeZone` pins it until a tenant provider exists.
const DATE_ONLY_FORMAT = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeZone: 'UTC',
});

// Cached per zone: constructing an `Intl.DateTimeFormat` is not cheap, and this runs once per
// date-time cell per render — a fresh one each time would be a per-row cost on every grid.
const dateTimeFormatters = new Map<string, Intl.DateTimeFormat>();

function dateTimeFormat(timeZone: string | undefined): Intl.DateTimeFormat {
  const key = timeZone ?? '';
  const cached = dateTimeFormatters.get(key);
  if (cached) return cached;

  const formatter = new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
    ...(timeZone === undefined ? {} : { timeZone }),
  });
  dateTimeFormatters.set(key, formatter);
  return formatter;
}

function rawUtc(date: Date): string {
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

export function DateCell(context: DataGridCellContext<unknown>): ReactNode {
  if (context.value === null || context.value === undefined) return null;

  const date =
    context.value instanceof Date ? context.value : new Date(context.value as string | number);
  if (Number.isNaN(date.getTime())) return null;

  if (context.meta?.dateFormat === 'date-time') {
    const utc = rawUtc(date);
    // The converted string alone cannot be checked against a log line or a colleague in another
    // zone, and does not say which zone it is in.
    //
    // `title` rather than `Tooltip`: `DataGridCell` already wraps this in one when
    // `meta.showTooltip` is set, and this cell is exported for use outside the grid, where no
    // `TooltipProvider` is mounted.
    return (
      <time dateTime={utc} title={utc}>
        {dateTimeFormat(context.meta.timeZone).format(date)}
      </time>
    );
  }

  return DATE_ONLY_FORMAT.format(date);
}
